// web/src/scoring/webScorer.ts
// FRAME Scoring Engine v2 — Real CV, full-range, memory-safe
// Replaces the broken v1 that produced clustered 5.5-7.0 scores
// Patent refs: Provisionals 1-7 (Feb-Apr 2026), Non-provisional claims 1-25

import type { PhotoScore, WeightProfile, DimensionScores, LandmarkTier } from '@frame/scoring-types';
import { computeComposite, computeCrowdPenalty } from '@frame/utils';

// ═══════════════════════════════════════════════════════════════
// SECTION 1: TF.js + MediaPipe Model Loading (unchanged, works)
// ═══════════════════════════════════════════════════════════════

let tf: any = null;
let nimaModel: any = null;
let nimaLoading = false;

async function loadTF() {
  if (tf) return tf;
  try {
    tf = await import('@tensorflow/tfjs');
    if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
      try { await tf.setBackend('webgpu'); } catch { await tf.setBackend('webgl'); }
    } else {
      await tf.setBackend('webgl');
    }
    await tf.ready();
    return tf;
  } catch (err) {
    console.warn('[FRAME] TF.js unavailable:', err);
    return null;
  }
}

async function loadNIMA(): Promise<boolean> {
  if (nimaModel) return true;
  if (nimaLoading) {
    while (nimaLoading) await new Promise(r => setTimeout(r, 100));
    return !!nimaModel;
  }
  nimaLoading = true;
  try {
    const tfjs = await loadTF();
    if (!tfjs) return false;
    nimaModel = await tfjs.loadGraphModel('/models/nima/model.json');
    return true;
  } catch {
    console.warn('[FRAME] NIMA not loaded — canvas-only scoring active');
    return false;
  } finally {
    nimaLoading = false;
  }
}

async function scoreWithNIMA(canvas: HTMLCanvasElement): Promise<number> {
  if (!nimaModel || !tf) return -1;
  try {
    return tf.tidy(() => {
      const small = document.createElement('canvas');
      small.width = 224; small.height = 224;
      small.getContext('2d')!.drawImage(canvas, 0, 0, 224, 224);
      const tensor = tf.browser.fromPixels(small)
        .toFloat().div(127.5).sub(1.0).expandDims(0);
      const output = nimaModel.predict(tensor);
      const dist = Array.from(output.dataSync()) as number[];
      small.width = 1; small.height = 1; // cleanup
      let mean = 0;
      for (let i = 0; i < dist.length; i++) mean += (i + 1) * dist[i];
      return mean;
    });
  } catch { return -1; }
}

let faceLandmarker: any = null;

async function loadFaceLandmarker() {
  if (faceLandmarker) return faceLandmarker;
  try {
    const vision = await import('@mediapipe/tasks-vision');
    const { FaceLandmarker, FilesetResolver } = vision;
    const resolver = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm'
    );
    faceLandmarker = await FaceLandmarker.createFromOptions(resolver, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      numFaces: 8,
      outputFaceBlendshapes: true,
    });
    return faceLandmarker;
  } catch (err) {
    console.warn('[FRAME] MediaPipe unavailable:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2: Screenshot Detection (Provisional 7, Claim 24)
// 5 independent signals — need 3+ to trigger gate
// ═══════════════════════════════════════════════════════════════

function detectScreenshot(data: Uint8ClampedArray, width: number, height: number): number {
  let confidence = 0;

  // Signal 1: Flat color blocks (UI backgrounds have near-zero variance)
  const blockSize = 16;
  let flatBlocks = 0, totalBlocks = 0;
  for (let by = 0; by < height - blockSize; by += blockSize) {
    for (let bx = 0; bx < width - blockSize; bx += blockSize) {
      let sum = 0, sumSq = 0, count = 0;
      for (let y = by; y < Math.min(by + blockSize, height); y++) {
        for (let x = bx; x < Math.min(bx + blockSize, width); x++) {
          const idx = (y * width + x) * 4;
          const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
          sum += lum; sumSq += lum * lum; count++;
        }
      }
      if (count > 0 && (sumSq / count) - (sum / count) ** 2 < 3.0) flatBlocks++;
      totalBlocks++;
    }
  }
  if (totalBlocks > 0 && flatBlocks / totalBlocks > 0.25) confidence += 0.25;

  // Signal 2: Phone screenshot aspect ratios (exclude panoramas)
  const ratio = Math.max(width, height) / Math.min(width, height);
  const isPanorama = ratio > 2.5;
  if (!isPanorama && [2.167, 2.170, 2.222, 2.333, 2.165].some(r => Math.abs(ratio - r) < 0.06)) {
    confidence += 0.20;
  }

  // Signal 3: Low unique colors (screenshots use flat palettes)
  const colorSet = new Set<number>();
  for (let i = 0; i < data.length; i += 16) {
    colorSet.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
  }
  if (colorSet.size / 4096 < 0.30) confidence += 0.20;

  // Signal 4: Status bar (top 7% uniform with sharp transition)
  const topH = Math.max(1, Math.round(height * 0.07));
  let topSum = 0, topSumSq = 0, topCount = 0;
  for (let y = 0; y < topH; y++) {
    for (let x = 0; x < width; x += 2) {
      const idx = (y * width + x) * 4;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      topSum += lum; topSumSq += lum * lum; topCount++;
    }
  }
  if (topCount > 0) {
    const topVar = (topSumSq / topCount) - (topSum / topCount) ** 2;
    if (topVar < 50) confidence += 0.15;
  }

  // Signal 5: Sensor noise absence (photos have noise std>3, screenshots std<1.5)
  const sx = Math.floor(width * 0.4), sy = Math.floor(height * 0.4);
  const sz = Math.min(32, Math.floor(width * 0.1), Math.floor(height * 0.1));
  let nSum = 0, nSumSq = 0, nCount = 0;
  for (let y = sy; y < sy + sz && y < height; y++) {
    for (let x = sx; x < sx + sz && x < width; x++) {
      const idx = (y * width + x) * 4;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      nSum += lum; nSumSq += lum * lum; nCount++;
    }
  }
  if (nCount > 0) {
    const noiseStd = Math.sqrt(Math.max(0, (nSumSq / nCount) - (nSum / nCount) ** 2));
    if (noiseStd < 1.5) confidence += 0.20;
  }

  return Math.min(1.0, confidence);
}

// ═══════════════════════════════════════════════════════════════
// SECTION 3: Single-Pass Pixel Analysis
// Histogram + Sobel + color stats in ONE iteration
// ═══════════════════════════════════════════════════════════════

interface PixelAnalysis {
  histogram: Float32Array;
  pixelCount: number;
  avgR: number; avgG: number; avgB: number;
  avgSat: number;
  sobelMag: Float32Array;
  sobelAngle: Float32Array;
  lumArray: Float32Array;
  screenshotConfidence: number;
}

function singlePassAnalysis(data: Uint8ClampedArray, width: number, height: number): PixelAnalysis {
  const pixelCount = width * height;
  const histogram = new Float32Array(256);
  const lumArray = new Float32Array(pixelCount);
  let rSum = 0, gSum = 0, bSum = 0, satSum = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    const pixIdx = i / 4;
    histogram[Math.min(255, lum)]++;
    lumArray[pixIdx] = lum;
    rSum += r; gSum += g; bSum += b;
    const maxC = Math.max(r, g, b), minC = Math.min(r, g, b);
    satSum += maxC > 0 ? (maxC - minC) / maxC : 0;
  }

  const sobelMag = new Float32Array(pixelCount);
  const sobelAngle = new Float32Array(pixelCount);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx = -lumArray[(y-1)*width+(x-1)] + lumArray[(y-1)*width+(x+1)]
               - 2*lumArray[y*width+(x-1)] + 2*lumArray[y*width+(x+1)]
               - lumArray[(y+1)*width+(x-1)] + lumArray[(y+1)*width+(x+1)];
      const gy = -lumArray[(y-1)*width+(x-1)] - 2*lumArray[(y-1)*width+x] - lumArray[(y-1)*width+(x+1)]
               + lumArray[(y+1)*width+(x-1)] + 2*lumArray[(y+1)*width+x] + lumArray[(y+1)*width+(x+1)];
      sobelMag[idx] = Math.sqrt(gx * gx + gy * gy);
      sobelAngle[idx] = Math.atan2(gy, gx);
    }
  }

  const screenshotConfidence = detectScreenshot(data, width, height);

  return {
    histogram, pixelCount, lumArray,
    avgR: rSum / pixelCount, avgG: gSum / pixelCount, avgB: bSum / pixelCount,
    avgSat: satSum / pixelCount,
    sobelMag, sobelAngle, screenshotConfidence,
  };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 4: Lighting — measurement-first, full 0-10 range
// ═══════════════════════════════════════════════════════════════

function scoreLighting(pa: PixelAnalysis): { score: number; note: string } {
  const { histogram, pixelCount, avgR, avgB } = pa;

  let shadowClip = 0, highlightClip = 0;
  for (let i = 0; i < 5; i++) shadowClip += histogram[i];
  for (let i = 251; i < 256; i++) highlightClip += histogram[i];
  const shadowPct = shadowClip / pixelCount;
  const highlightPct = highlightClip / pixelCount;
  const exposureQuality = 1.0 - Math.min(1, shadowPct * 5 + highlightPct * 8);

  let p1 = 0, p99 = 255, cum = 0;
  for (let i = 0; i < 256; i++) {
    cum += histogram[i];
    if (cum >= pixelCount * 0.01 && p1 === 0) p1 = i;
    if (cum >= pixelCount * 0.99) { p99 = i; break; }
  }
  const rangeQuality = Math.min(1, (p99 - p1) / 200);

  let lumMean = 0;
  for (let i = 0; i < 256; i++) lumMean += i * histogram[i];
  lumMean /= pixelCount;
  let lumVar = 0;
  for (let i = 0; i < 256; i++) lumVar += histogram[i] * (i - lumMean) ** 2;
  const stdDev = Math.sqrt(lumVar / pixelCount);
  const contrastQuality = stdDev < 20 ? 0.2 : stdDev < 40 ? 0.5 + (stdDev - 20) / 40 :
                          stdDev < 70 ? 1.0 : stdDev < 90 ? 0.8 : 0.5;

  const rbRatio = avgR / Math.max(avgB, 0.01);
  const warmth = (rbRatio >= 1.1 && rbRatio <= 1.6) ? 1.0 : (rbRatio >= 0.9 && rbRatio <= 1.8) ? 0.7 : 0.4;

  const raw = (exposureQuality * 0.35 + rangeQuality * 0.25 + contrastQuality * 0.25 + warmth * 0.15) * 10;

  let note = '';
  if (highlightPct > 0.05) note = 'Significant highlight clipping';
  else if (shadowPct > 0.10) note = 'Underexposed — dark shadows';
  else if (stdDev < 25) note = 'Low contrast — flat or washed out';
  else if (raw >= 7) note = 'Well-lit with good tonal range';
  else if (raw >= 5) note = 'Acceptable lighting';
  else note = 'Poor lighting conditions';

  return { score: Math.max(0, Math.min(10, raw)), note };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 5: Composition — strategy detection + penalties
// ═══════════════════════════════════════════════════════════════

function scoreComposition(pa: PixelAnalysis, width: number, height: number): { score: number; note: string } {
  const { sobelMag, sobelAngle } = pa;
  const salThreshold = 40;

  let thirdsHits = 0, totalSalient = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (sobelMag[idx] > salThreshold) {
        totalSalient++;
        const xr = x / width, yr = y / height;
        if ((Math.abs(xr - 0.333) < 0.06 || Math.abs(xr - 0.667) < 0.06) ||
            (Math.abs(yr - 0.333) < 0.06 || Math.abs(yr - 0.667) < 0.06)) {
          thirdsHits++;
        }
      }
    }
  }
  const thirdsScore = totalSalient > 0 ? Math.min(1, (thirdsHits / totalSalient) / 0.30) : 0.3;

  let leftWeight = 0, rightWeight = 0;
  const halfW = Math.floor(width / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < halfW; x++) {
      leftWeight += sobelMag[y * width + x];
      rightWeight += sobelMag[y * width + (width - 1 - x)];
    }
  }
  const total = leftWeight + rightWeight;
  const symmetryScore = total > 0 ? 1.0 - Math.abs(leftWeight - rightWeight) / total : 0.5;

  const strategyScore = Math.max(thirdsScore, symmetryScore);
  const strategyName = thirdsScore >= symmetryScore ? 'Rule of thirds' : 'Centered symmetry';

  const hAngles: number[] = [];
  for (let i = 0; i < sobelMag.length; i++) {
    if (sobelMag[i] > 40) {
      const deg = sobelAngle[i] * (180 / Math.PI);
      if (Math.abs(deg) > 70 && Math.abs(deg) < 110) hAngles.push(deg - 90);
    }
  }
  hAngles.sort((a, b) => a - b);
  const tilt = hAngles.length > 0 ? Math.abs(hAngles[Math.floor(hAngles.length / 2)]) : 0;
  const tiltPenalty = Math.min(2.5, tilt * 0.4);

  let edgeCount = 0;
  for (let i = 0; i < sobelMag.length; i++) { if (sobelMag[i] > 30) edgeCount++; }
  const clutterRatio = edgeCount / (width * height);
  const clutterPenalty = clutterRatio > 0.30 ? 2.0 : clutterRatio > 0.20 ? 1.0 : clutterRatio > 0.12 ? 0.5 : 0;

  const raw = Math.max(0, strategyScore * 10 - tiltPenalty - clutterPenalty);
  const note = `${strategyName} composition${tilt > 3 ? ', slight tilt detected' : ''}`;

  return { score: Math.max(0, Math.min(10, raw)), note };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 6: Scene Interest — vibrancy + complexity + NIMA blend
// ═══════════════════════════════════════════════════════════════

function scoreSceneInterest(pa: PixelAnalysis, nimaScore: number): { score: number; note: string } {
  const { avgSat, sobelMag } = pa;

  const vibrancy = Math.min(1, avgSat * 2.5);

  let edgeCount = 0;
  for (let i = 0; i < sobelMag.length; i++) { if (sobelMag[i] > 25) edgeCount++; }
  const edgeRatio = edgeCount / sobelMag.length;
  const complexity = edgeRatio < 0.03 ? 0.2 : edgeRatio < 0.08 ? 0.6 :
                     edgeRatio < 0.18 ? 1.0 : edgeRatio < 0.30 ? 0.7 : 0.4;

  const canvasRaw = (vibrancy * 0.5 + complexity * 0.5) * 10;

  let raw: number;
  if (nimaScore > 0) {
    raw = canvasRaw * 0.4 + nimaScore * 0.6;
  } else {
    raw = canvasRaw;
  }

  const note = raw >= 7 ? 'Visually engaging scene' : raw >= 5 ? 'Moderate visual interest' :
               raw >= 3 ? 'Limited visual interest' : 'Very low visual interest';

  return { score: Math.max(0, Math.min(10, raw)), note };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 7: Portrait Composition — multi-face, Duchenne, relevance
// ═══════════════════════════════════════════════════════════════

interface FaceResult {
  score: number;
  faceCount: number;
  faceRelevance: number;
  note: string;
}

function scoreFaces(landmarker: any, canvas: HTMLCanvasElement, imgW: number, imgH: number): FaceResult {
  if (!landmarker) return { score: 0, faceCount: 0, faceRelevance: 0, note: '' };

  try {
    const result = landmarker.detect(canvas);
    const faces = result.faceLandmarks ?? [];
    const faceCount = faces.length;

    if (faceCount === 0) return { score: 0, faceCount: 0, faceRelevance: 0, note: 'No faces detected' };

    const perFaceScores = faces.map((lms: any, idx: number) => {
      let fs = 5.0;
      const bs = result.faceBlendshapes?.[idx]?.categories ?? [];
      const getBS = (name: string) => bs.find((b: any) => b.categoryName === name)?.score ?? 0;

      const mouthSmile = (getBS('mouthSmileLeft') + getBS('mouthSmileRight')) / 2;
      const eyeCrinkle = (getBS('cheekSquintLeft') + getBS('cheekSquintRight')) / 2;
      if (mouthSmile > 0.4 && eyeCrinkle > 0.2) fs += 3.0;
      else if (mouthSmile > 0.3) fs += 1.5;
      else if (mouthSmile < 0.1) fs += 0.5;

      const blinkL = getBS('eyeBlinkLeft'), blinkR = getBS('eyeBlinkRight');
      if (blinkL > 0.5 && blinkR > 0.5) fs -= 3.0;
      else if (blinkL > 0.5 || blinkR > 0.5) fs -= 1.5;
      else fs += 0.5;

      const gazeIn = Math.abs(getBS('eyeLookInLeft') - getBS('eyeLookInRight'));
      if (gazeIn < 0.15) fs += 0.5;

      if (lms?.length > 10) {
        const eyeY = (lms[468]?.y ?? lms[4]?.y ?? 0.5);
        const thirdsDeviation = Math.abs(eyeY - 0.333);
        fs += Math.max(0, (0.15 - thirdsDeviation) * 10);
      }

      return Math.max(0, Math.min(10, fs));
    });

    const worstScore = Math.min(...perFaceScores);
    const avgScore = perFaceScores.reduce((a: number, b: number) => a + b, 0) / perFaceScores.length;
    const compositeScore = faceCount === 1 ? perFaceScores[0] : worstScore * 0.6 + avgScore * 0.4;

    const faceAreas = faces.map((lms: any) => {
      if (!lms || lms.length < 10) return 0;
      const xs = lms.map((l: any) => l.x), ys = lms.map((l: any) => l.y);
      return (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
    });
    const largestFaceRatio = Math.max(...faceAreas);
    const faceRelevance = largestFaceRatio < 0.02 ? 0.1 : largestFaceRatio < 0.08 ? 0.4 :
                          largestFaceRatio < 0.25 ? 0.8 : 1.0;

    const note = faceCount === 1 ?
      (compositeScore >= 7 ? 'Natural, well-composed expression' :
       compositeScore >= 5 ? 'Acceptable portrait composition' : 'Expression could improve') :
      `${faceCount} faces detected — ${worstScore < 5 ? 'one subject could improve' : 'good group shot'}`;

    return { score: Math.max(0, Math.min(10, compositeScore)), faceCount, faceRelevance, note };
  } catch {
    return { score: 0, faceCount: 0, faceRelevance: 0, note: '' };
  }
}

// ═══════════════════════════════════════════════════════════════
// SECTION 8: Calibration — maps raw cluster to full 0-10 range
// ═══════════════════════════════════════════════════════════════

const CALIBRATION: { raw: number; cal: number }[] = [
  { raw: 0, cal: 0 }, { raw: 2, cal: 0.5 }, { raw: 3, cal: 1.5 },
  { raw: 4, cal: 2.5 }, { raw: 5, cal: 3.5 }, { raw: 5.5, cal: 4.5 },
  { raw: 6, cal: 5.0 }, { raw: 6.5, cal: 6.0 }, { raw: 7, cal: 7.0 },
  { raw: 7.5, cal: 7.5 }, { raw: 8, cal: 8.0 }, { raw: 8.5, cal: 8.5 },
  { raw: 9, cal: 9.0 }, { raw: 10, cal: 10 },
];

function calibrate(raw: number): number {
  if (raw <= CALIBRATION[0].raw) return CALIBRATION[0].cal;
  if (raw >= CALIBRATION[CALIBRATION.length - 1].raw) return CALIBRATION[CALIBRATION.length - 1].cal;
  for (let i = 0; i < CALIBRATION.length - 1; i++) {
    if (raw >= CALIBRATION[i].raw && raw <= CALIBRATION[i + 1].raw) {
      const t = (raw - CALIBRATION[i].raw) / (CALIBRATION[i + 1].raw - CALIBRATION[i].raw);
      return CALIBRATION[i].cal + t * (CALIBRATION[i + 1].cal - CALIBRATION[i].cal);
    }
  }
  return raw;
}

// ═══════════════════════════════════════════════════════════════
// SECTION 9: Public API — Memory-safe batch processing
// Sequential + canvas cleanup + GC yield (fixes Safari crash)
// ═══════════════════════════════════════════════════════════════

export interface WebScoringOptions {
  weights: WeightProfile;
  onProgress?: (current: number, total: number) => void;
}

export async function scorePhotosWeb(
  imageFiles: File[],
  options: WebScoringOptions,
): Promise<PhotoScore[]> {
  const [nimaAvailable, landmarker] = await Promise.all([
    loadNIMA(),
    loadFaceLandmarker(),
  ]);

  const scores: PhotoScore[] = [];

  for (let i = 0; i < imageFiles.length; i++) {
    const file = imageFiles[i];
    const start = performance.now();
    let canvas: HTMLCanvasElement | null = null;
    let thumbnailUrl: string | null = null;

    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as any);

      canvas = document.createElement('canvas');
      const scale = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();

      const imageData = canvas.getContext('2d')!.getImageData(0, 0, w, h);
      const pa = singlePassAnalysis(imageData.data, w, h);

      const isScreenshot = pa.screenshotConfidence > 0.6;

      const lightingResult = scoreLighting(pa);
      const compositionResult = scoreComposition(pa, w, h);
      const nimaScore = nimaAvailable ? await scoreWithNIMA(canvas) : -1;
      const sceneResult = scoreSceneInterest(pa, nimaScore);
      const faceResult = scoreFaces(landmarker, canvas, w, h);

      let lightingCal = calibrate(lightingResult.score);
      let compositionCal = calibrate(compositionResult.score);
      let sceneCal = calibrate(sceneResult.score);
      let portraitCal = faceResult.faceCount > 0 ? calibrate(faceResult.score) : null;

      if (pa.screenshotConfidence > 0.8) {
        lightingCal = Math.min(lightingCal, 1.5);
        compositionCal = Math.min(compositionCal, 1.5);
        sceneCal = Math.min(sceneCal, 1.5);
        if (portraitCal !== null) portraitCal = Math.min(portraitCal, 1.5);
      } else if (pa.screenshotConfidence > 0.6) {
        const penalty = pa.screenshotConfidence * 3.0;
        lightingCal = Math.max(0, lightingCal - penalty);
        compositionCal = Math.max(0, compositionCal - penalty);
        sceneCal = Math.max(0, sceneCal - penalty);
        if (portraitCal !== null) portraitCal = Math.max(0, portraitCal - penalty);
      }

      const dimensions: DimensionScores = {
        portraitComposition: portraitCal,
        lighting: lightingCal,
        composition: compositionCal,
        sceneInterest: sceneCal,
        sceneInterestSpiked: sceneCal,
      };

      const crowdPenalty = computeCrowdPenalty(faceResult.faceCount);
      const { rawComposite, finalComposite } = computeComposite(dimensions, options.weights, crowdPenalty);

      thumbnailUrl = URL.createObjectURL(file);

      const notes: Record<string, string> = {};
      if (lightingResult.note) notes.lighting = lightingResult.note;
      if (compositionResult.note) notes.composition = compositionResult.note;
      if (sceneResult.note) notes.sceneInterest = sceneResult.note;
      if (faceResult.note) notes.portraitComposition = faceResult.note;
      if (isScreenshot) notes._screenshot = `Non-photo detected (${Math.round(pa.screenshotConfidence * 100)}% confidence)`;

      scores.push({
        id: `web_${i}`,
        assetIdentifier: file.name,
        dimensions,
        landmarkTier: 0 as LandmarkTier,
        detractorPenalty: crowdPenalty,
        detectedFaceCount: faceResult.faceCount,
        rawCompositeScore: rawComposite,
        compositeScore: finalComposite,
        dimensionNotes: notes,
        isSelected: false,
        processingDurationMs: performance.now() - start,
        thumbnailUri: thumbnailUrl,
      });

    } catch (err) {
      console.error(`[FRAME] Error scoring ${file.name}:`, err);
      scores.push({
        id: `web_${i}`,
        assetIdentifier: file.name,
        dimensions: { portraitComposition: null, lighting: 0, composition: 0, sceneInterest: 0, sceneInterestSpiked: 0 },
        landmarkTier: 0 as LandmarkTier,
        detractorPenalty: 0,
        detectedFaceCount: 0,
        rawCompositeScore: 0,
        compositeScore: 0,
        dimensionNotes: { _error: 'Failed to process' },
        isSelected: false,
        processingDurationMs: performance.now() - start,
        thumbnailUri: URL.createObjectURL(file),
      });
    } finally {
      if (canvas) { canvas.width = 1; canvas.height = 1; canvas = null; }
      await new Promise(r => setTimeout(r, 30));
    }

    options.onProgress?.(i + 1, imageFiles.length);
  }

  scores.sort((a, b) => b.compositeScore - a.compositeScore);

  scores.forEach((s, idx) => {
    const percentile = Math.round(100 - (idx / scores.length * 100));
    s.dimensionNotes._percentile = `Top ${Math.max(1, percentile)}% of this session`;
  });

  return scores;
}
